<template>
  <div ref="chartContainer" style="width: 100%; height: 100%;">
    <highcharts class="mt-4" :options="options" ref="aChart"
     style="width: 100%; height: 100%;"></highcharts>
  </div>
</template>
<script>
import { mapState } from 'vuex';

export default {
  name: 'ShiftWiseProductionCount',
  props: {
    thisMachines: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    this.chart = this.$refs.aChart.chart;
    this.createSeries();
    this.initResizeObserver();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  data() {
    return {
      resizeObserver: null,
      chart: null,
      options: {
        chart: {
          type: 'column',
          style: {
            fontFamily: 'Poppins, sans-serif',
          },
        },
        title: {
          text: 'Shift Wise Production Count',
          style: {
            color: '',
          },
        },
        plotOptions: {
          series: {
            grouping: true,
            borderWidth: 0,
          },
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          itemStyle: {
            color: '#333',
            fontWeight: 'bold',
          },
        },
        tooltip: {
          shared: true,
        },
        xAxis: {
          type: 'category',
          labels: {
            style: {
              color: '',
            },
          },
        },
        yAxis: [
          {
            title: {
              text: null,
            },
            labels: {
              style: {
                color: '',
              },
            },
          },
        ],
        series: [{}],
      },
    };
  },
  computed: {
    ...mapState('helper', ['isDark']),
  },
  methods: {
    getColor(key) {
      const colors = {
        'Shift A-target': '#3D519D',
        'Shift A-produced': '#3D519D',
        'Shift B-target': '#1CA4D7',
        'Shift B-produced': '#1CA4D7',
        'Shift C-target': '#5F4E94',
        'Shift C-produced': '#5F4E94',
      };
      // Part-specific overrides
      if (key.includes('GEN3')) return 'rgba(3, 139, 190)';
      if (key.includes('EBNA')) return 'rgba(95, 245, 242)';
      if (key.includes('Cylinder Block')) return 'rgba(255, 140, 0)';
      if (key.includes('Cylinder Head')) return 'rgba(153, 50, 204)';
      if (key.includes('Crank Shaft')) return 'rgba(100, 149, 237)';
      if (key.includes('NA')) return 'rgba(146, 149, 158)';
      return colors[key] || this.getRandomColor();
    },
    getRandomColor() {
      // Generate random but soft colors
      const r = Math.floor(Math.random() * 200);
      const g = Math.floor(Math.random() * 200);
      const b = Math.floor(Math.random() * 200);
      return `rgba(${r},${g},${b},0.8)`;
    },
    roundOff(val) {
      if (val) {
        return Math.round((val + Number.EPSILON) * 100) / 100;
      }
      return 0;
    },
    createSeries() {
      const grouped = {};
      const shiftSet = new Set();
      const partSet = new Set();
      const dateSet = new Set();

      // Group data by machine → shift → date → part
      this.thisMachines.forEach((entry) => {
        shiftSet.add(entry.shift);
        dateSet.add(entry.date);
        Object.keys(entry.partnames).forEach((part) => partSet.add(part));

        if (!grouped[entry.machinename]) {
          grouped[entry.machinename] = {};
        }
        if (!grouped[entry.machinename][entry.shift]) {
          grouped[entry.machinename][entry.shift] = {};
        }
        if (!grouped[entry.machinename][entry.shift][entry.date]) {
          grouped[entry.machinename][entry.shift][entry.date] = {};
        }

        Object.entries(entry.partnames).forEach(([partname, produced]) => {
          if (!grouped[entry.machinename][entry.shift][entry.date][partname]) {
            grouped[entry.machinename][entry.shift][entry.date][partname] = {
              produced: 0,
              target: entry.target,
            };
          }

          // Sum the produced quantities
          grouped[entry.machinename][entry.shift][entry.date][partname].produced += produced;
        });
      });

      const shifts = Array.from(shiftSet).sort();
      const partnames = Array.from(partSet).sort();
      const dates = Array.from(dateSet).sort((a, b) => new Date(a.split('-').reverse().join('-')) - new Date(b.split('-').reverse().join('-')));
      const categories = Object.keys(grouped); // machines
      const series = [];

      shifts.forEach((shift) => {
        dates.forEach((date) => {
          // Target line per date
          const targetData = categories.map((machine) => {
            const machineShiftDate = grouped[machine][shift]?.[date];
            if (machineShiftDate) {
              const firstPart = Object.values(machineShiftDate)[0];
              return firstPart ? firstPart.target : 0;
            }
            return 0;
          });

          series.push({
            name: `${shift} - Target (${date})`,
            data: targetData,
            type: 'line',
            color: this.getColor(`${shift}-target-${date}`),
            marker: { enabled: true, radius: 3 },
            zIndex: 5,
            dataLabels: {
              enabled: true,
              style: { fontWeight: 'bold', color: '#000' },
              // eslint-disable-next-line
              formatter: function () {
                return this.y;
              },
            },
          });

          // Produced columns per part
          partnames.forEach((part) => {
            const producedData = categories.map((machine) => {
              const machineShiftDate = grouped[machine][shift]?.[date];
              const partData = machineShiftDate ? machineShiftDate[part] : null;
              return partData ? partData.produced : '';
            });

            series.push({
              name: `${shift} - ${part} (${date})`,
              data: producedData,
              type: 'column',
              stack: `${shift}-${date}`,
              color: this.getColor(`${shift}-${part}-${date}`),
              dataLabels: {
                enabled: true,
                style: { fontWeight: 'bold', color: '#000' },
                // eslint-disable-next-line
                formatter: function () {
                  return this.y && this.y !== 0 ? this.y : null;
                },
              },
            });
          });
        });
      });

      this.options.xAxis.categories = categories;
      this.options.series = series;
      this.options.plotOptions.column = {
        stacking: 'normal',
      };

      this.$nextTick(() => this.reflowChart());
    },

    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.reflowChart();
      });
      if (this.$refs.chartContainer) {
        this.resizeObserver.observe(this.$refs.chartContainer);
      }
    },
    reflowChart() {
      this.$nextTick(() => {
        const chartInstance = this.$refs.aChart?.chart;
        if (chartInstance?.reflow) chartInstance.reflow();
      });
    },
  },
  watch: {
    thisMachines: {
      deep: true,
      handler() {
        this.createSeries();
      },
    },
    isDark: {
      immediate: true,
      handler(val) {
        const color = val ? '#FFFFFF' : '#333333';
        const labelColor = val ? '#FFFFFF' : '#666666';
        const seriesColor = val ? '#21C77C' : '#354493';

        this.options.title.style.color = color;
        this.options.yAxis[0].labels.style.color = labelColor;
        this.options.xAxis.labels.style.color = labelColor;
        if (this.options.series[0]) {
          this.options.series[0].color = seriesColor;
        }
      },
    },
  },
};
</script>
